import { AnimatePresence, motion } from "framer-motion";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  CircleHelp,
  DollarSign,
  ExternalLink,
  HelpCircle,
  Info,
  MessageSquare,
  Play,
  ScrollText,
  ShieldAlert,
  Workflow,
  Wrench,
  X,
} from "lucide-react";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { OVERLAY_LABELS, riskCategoryToOverlay } from "@/lib/arch-overlay";
import type {
  Actor,
  ApiEndpoint,
  ArchitectureData,
  Assumption,
  Component,
  Container,
  DataFlow,
  Decision,
  Entity,
  ExternalSystem,
  OperationalNote,
  Permission,
  Question,
  Risk,
  System,
} from "@/lib/arch-parser";
import { renderMarkdown } from "@/lib/arch-parser/parse-markdown";
import { cn } from "@/lib/utils";
import { useArchViz } from "./ArchVizContext";
import { CommentsSection } from "./CommentsSection";
import { LEGACY_ORIGIN_CARD_CLASSES, LegacyOriginChip } from "./LegacyOriginChip";
import { brandIconSlugFromName, brandIconUrl } from "./nodes/brand-icons";
import { QuestionPanel } from "./QuestionPanel";

const MIN_WIDTH = 320;
const MAX_WIDTH = 880;
const DEFAULT_WIDTH = 380;
const WIDTH_STORAGE_KEY = "archviz:detail-panel-width";

function loadInitialWidth(): number {
  if (typeof window === "undefined") return DEFAULT_WIDTH;
  const raw = window.localStorage.getItem(WIDTH_STORAGE_KEY);
  if (!raw) return DEFAULT_WIDTH;
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n)) return DEFAULT_WIDTH;
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, n));
}

type Resolved =
  | { kind: "system"; entity: System }
  | { kind: "actor"; entity: Actor }
  | { kind: "external-system"; entity: ExternalSystem }
  | { kind: "container"; entity: Container }
  | { kind: "component"; entity: Component; container?: Container }
  | { kind: "entity"; entity: Entity; container?: Container }
  | { kind: "question"; entity: Question }
  | { kind: "permission"; entity: Permission; external?: ExternalSystem }
  | { kind: "endpoint"; entity: ApiEndpoint; external?: ExternalSystem }
  | null;

function resolveSelection(arch: ArchitectureData, id: string | null): Resolved {
  if (!id) return null;
  // v0.3 architectures author a real System entity (arch.system). v0.2
  // architectures don't — the SystemBoundaryNode used to be unselectable
  // upstream, but gap #4/#5 made it selectable so the description + drill
  // CTA can live in the side panel. For the v0.2 case we synthesize a
  // System-shaped entity from arch.meta so the rest of this component
  // doesn't need a v0.2-specific code path.
  if (id === "system") {
    if (arch.system) return { kind: "system", entity: arch.system };
    const synthesized: System = {
      id: "system",
      label: arch.meta.name,
      description: arch.meta.description,
    };
    return { kind: "system", entity: synthesized };
  }
  const [prefix, rawId] = id.split(":");
  if (!rawId) return null;
  if (prefix === "actor") {
    const actor = arch.actors.find((a) => a.id === rawId);
    return actor ? { kind: "actor", entity: actor } : null;
  }
  if (prefix === "external") {
    const ext = arch.externalSystems.find((e) => e.id === rawId);
    return ext ? { kind: "external-system", entity: ext } : null;
  }
  if (prefix === "container") {
    const c = arch.containers.find((cc) => cc.id === rawId);
    return c ? { kind: "container", entity: c } : null;
  }
  if (prefix === "component") {
    // Legacy v0.2 docs nest components under containers; v0.3 lifts them
    // to arch.components[] with each carrying `container: <id>`. Walk both
    // — the mapper populates the nested form for v0.2 → canonical, so
    // either lookup hits.
    for (const c of arch.containers) {
      const comp = c.components.find((cc) => cc.id === rawId);
      if (comp) return { kind: "component", entity: comp, container: c };
    }
    const top = arch.components?.find((cc) => cc.id === rawId);
    if (top) {
      const parent = arch.containers.find((cc) => cc.id === top.container);
      return { kind: "component", entity: top, container: parent };
    }
  }
  if (prefix === "entity") {
    // Entities live inside a container's data model. Walk the containers and
    // surface the parent on the result so the panel header reads
    // "Entity · <container>" — same convention as Component → Container.
    for (const c of arch.containers) {
      const ent = c.dataModel?.entities.find((e) => e.id === rawId);
      if (ent) return { kind: "entity", entity: ent, container: c };
    }
    return null;
  }
  if (prefix === "question") {
    const q = arch.questions?.find((qq) => qq.id === rawId);
    return q ? { kind: "question", entity: q } : null;
  }
  if (prefix === "permission") {
    // permission ids are scope strings, which can contain dots — rejoin
    // anything past the first colon in case the scope itself had one.
    const scope = id.slice(prefix.length + 1);
    for (const ext of arch.externalSystems) {
      const perm = ext.permissions?.find((p) => p.scope === scope);
      if (perm) return { kind: "permission", entity: perm, external: ext };
    }
    return null;
  }
  if (prefix === "endpoint") {
    for (const ext of arch.externalSystems) {
      const endpoint = ext.endpoints?.find((e) => e.id === rawId);
      if (endpoint) return { kind: "endpoint", entity: endpoint, external: ext };
    }
    return null;
  }
  return null;
}

function TechBadge({ tech }: { tech: string }) {
  const slug = brandIconSlugFromName(tech);
  const [failed, setFailed] = useState(false);
  return (
    <Badge variant="secondary" className="font-mono text-[10px] gap-1.5 pl-1.5">
      {slug && !failed && (
        <img
          src={brandIconUrl(slug)}
          alt=""
          loading="lazy"
          className="h-3 w-3 object-contain"
          onError={() => setFailed(true)}
        />
      )}
      <span>{tech}</span>
    </Badge>
  );
}

function TechnologyBadges({ technology }: { technology: string[] }) {
  if (technology.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {technology.map((t) => (
        <TechBadge key={t} tech={t} />
      ))}
    </div>
  );
}

function NarrativeBlock({ markdown }: { markdown: string | undefined }) {
  if (!markdown) return null;
  const html = renderMarkdown(markdown);
  return (
    <div
      className="prose-sm max-w-none text-sm border-l-2 border-primary/30 pl-3 py-1 text-muted-foreground [&_table]:text-xs [&_table]:my-2 [&_table]:border-collapse [&_th]:border [&_th]:px-2 [&_th]:py-1 [&_td]:border [&_td]:px-2 [&_td]:py-1 [&_code]:font-mono [&_code]:text-xs [&_strong]:text-foreground [&_p]:my-1"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

// Risk category → human label. Overlay-bearing categories share OVERLAY_LABELS;
// operability / vendor-lock-in / other don't surface in the overlay UI and
// get a literal label here.
const RISK_CATEGORY_LABELS: Record<Risk["category"], string> = {
  cost: OVERLAY_LABELS.cost_impact,
  scalability: OVERLAY_LABELS.scale_performance,
  security: OVERLAY_LABELS.security,
  compliance: OVERLAY_LABELS.compliance,
  "data-readiness": OVERLAY_LABELS.data_readiness,
  "data-connectivity": OVERLAY_LABELS.data_connectivity,
  operability: "Operability",
  "vendor-lock-in": "Vendor lock-in",
  other: "Other",
};

const RISK_RESPONSE_LABELS: Record<Risk["response"]["kind"], string> = {
  mitigation: "Mitigation",
  alternative: "Alternative",
  accepted_because: "Accepted because",
};

// The response is the *answer* to the risk, not the risk itself — so it
// shouldn't read as alarming red. Forward-looking solutions (mitigation,
// alternative) get a positive green tone; an accepted risk stays neutral.
const RISK_RESPONSE_TONE: Record<Risk["response"]["kind"], string> = {
  mitigation: "border-success/40 text-success-ink",
  alternative: "border-success/40 text-success-ink",
  accepted_because: "border-border text-ink-muted",
};

function RiskSeverityChip({ severity }: { severity: Risk["severity"] }) {
  const cls =
    severity === "critical"
      ? "bg-danger-soft text-danger-ink"
      : severity === "warning"
        ? "bg-warning-soft text-warning-ink"
        : "bg-info-soft text-info-ink";
  return (
    <span
      className={cn(
        "text-[9px] uppercase tracking-wider rounded-full px-1.5 py-0.5 font-medium",
        cls,
      )}
    >
      {severity}
    </span>
  );
}

function RiskCards({ risks }: { risks: readonly Risk[] }) {
  if (risks.length === 0) return null;
  return (
    <div className="space-y-2">
      {risks.map((r, idx) => {
        const isLegacy = Boolean(r.legacy_origin);
        // Stable enough key for a derived list: category+description+index.
        // Risks don't carry IDs.
        const key = `${r.category}:${r.description.slice(0, 32)}:${idx}`;
        const overlayCat = riskCategoryToOverlay(r.category);
        const categoryLabel = r.category_label ?? RISK_CATEGORY_LABELS[r.category];
        return (
          <div
            key={key}
            className={cn(
              "text-xs px-3 py-2 flex items-start gap-2",
              isLegacy
                ? LEGACY_ORIGIN_CARD_CLASSES
                : "rounded-md border border-danger/30 bg-danger-soft",
            )}
          >
            <AlertCircle
              className={cn(
                "h-3.5 w-3.5 mt-0.5 shrink-0",
                isLegacy ? "text-ink-subtle" : "text-danger",
              )}
            />
            <div className="flex-1 min-w-0 space-y-1.5">
              <div className="flex items-center gap-1.5 flex-wrap">
                <Badge
                  variant="outline"
                  className={cn(
                    "text-[9px] uppercase tracking-wider shrink-0",
                    isLegacy ? "border-border text-ink-muted" : "border-danger/30 text-danger",
                  )}
                  title={overlayCat ? OVERLAY_LABELS[overlayCat] : categoryLabel}
                >
                  {categoryLabel}
                </Badge>
                <RiskSeverityChip severity={r.severity} />
                {isLegacy && <LegacyOriginChip />}
              </div>
              <div
                className={cn(
                  "prose-sm max-w-none [&_p]:my-0 [&_strong]:font-semibold [&_code]:font-mono [&_code]:text-[11px] [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded",
                  isLegacy
                    ? "text-ink [&_code]:bg-surface-tint"
                    : "text-danger-ink [&_code]:bg-danger-soft",
                )}
                dangerouslySetInnerHTML={{ __html: renderMarkdown(r.description) }}
              />
              <div
                className={cn(
                  "text-[11px] border-l-2 pl-2",
                  isLegacy ? "border-border text-ink-muted" : RISK_RESPONSE_TONE[r.response.kind],
                )}
              >
                <span className="font-medium uppercase tracking-wide text-[9px] mr-1">
                  {RISK_RESPONSE_LABELS[r.response.kind]}:
                </span>
                <span
                  className="prose-sm max-w-none inline [&_p]:my-0 [&_p]:inline [&_strong]:font-semibold [&_code]:font-mono"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(r.response.text) }}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AssumptionsList({ assumptions }: { assumptions: readonly Assumption[] }) {
  if (assumptions.length === 0) return null;
  return (
    <div className="space-y-2">
      {assumptions.map((a, idx) => {
        const key = `${a.description.slice(0, 32)}:${idx}`;
        const tone = a.verified
          ? "border-success/30 bg-success-soft"
          : "border-warning/30 bg-warning-soft";
        const Icon = a.verified ? CheckCircle2 : CircleHelp;
        const iconCls = a.verified ? "text-success" : "text-warning";
        return (
          <div
            key={key}
            className={cn("text-xs rounded-md border px-3 py-2 flex items-start gap-2", tone)}
          >
            <Icon className={cn("h-3.5 w-3.5 mt-0.5 shrink-0", iconCls)} />
            <div className="flex-1 min-w-0 space-y-1">
              <div
                className="prose-sm max-w-none text-foreground/90 [&_p]:my-0 [&_strong]:font-semibold [&_code]:font-mono"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(a.description) }}
              />
              {!a.verified && a.how_to_verify && (
                <div className="text-[11px] italic text-muted-foreground border-l-2 border-warning/40 pl-2">
                  <span className="font-medium uppercase tracking-wide text-[9px] not-italic mr-1">
                    How to verify:
                  </span>
                  {a.how_to_verify}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CostIndicator({ cost }: { cost: string | boolean | undefined }) {
  if (cost === undefined || cost === false) return null;
  const text = typeof cost === "string" ? cost : "Has associated costs";
  return (
    <div className="text-xs rounded-md border border-warning/30 bg-warning-soft px-3 py-2 flex items-center gap-2">
      <DollarSign className="h-3.5 w-3.5 text-warning shrink-0" />
      <span className="text-warning-ink">{text}</span>
    </div>
  );
}

function DecisionBadge({ status }: { status: string }) {
  const s = status.toLowerCase();
  const cls = s.includes("accepted")
    ? "bg-success-soft text-success-ink"
    : s === "proposed"
      ? "bg-info-soft text-info-ink"
      : s === "deprecated" || s === "superseded"
        ? "bg-surface-tint text-ink-muted"
        : "bg-warning-soft text-warning-ink";
  return (
    <span className={cn("text-[10px] uppercase tracking-wide rounded px-2 py-0.5", cls)}>
      {status}
    </span>
  );
}

function DecisionCards({ decisions }: { decisions: Decision[] }) {
  if (decisions.length === 0) return null;
  return (
    <div className="space-y-2">
      {decisions.map((d) => {
        const heading = d.title ?? d.label;
        // v0.3: options_considered is a string[] with a separate `chosen`.
        // v0.2: optionsConsidered is a single string. Render whichever
        // populated; never render both.
        const v3Options = d.options_considered;
        const v2Options = d.optionsConsidered;
        return (
          <details key={d.id} className="text-xs rounded-md border bg-card px-3 py-2">
            <summary className="cursor-pointer flex items-center justify-between gap-2">
              <span className="font-medium truncate">{heading}</span>
              <div className="flex items-center gap-1.5 shrink-0">
                {d.superseded_by && (
                  <span
                    className="text-[9px] uppercase tracking-wider rounded-sm px-1.5 py-0.5 bg-surface-tint text-ink-muted"
                    title={`Superseded by ${d.superseded_by}`}
                  >
                    superseded
                  </span>
                )}
                <DecisionBadge status={d.status} />
              </div>
            </summary>
            {d.why && (
              <div className="mt-1.5 text-[11px] italic text-muted-foreground border-l-2 border-primary/30 pl-2">
                {d.why}
              </div>
            )}
            <div className="mt-2 space-y-2 text-muted-foreground">
              {d.chosen && (
                <div>
                  <div className="font-medium text-foreground text-[10px] uppercase tracking-wide mb-0.5">
                    Chosen
                  </div>
                  <div className="text-foreground/90 rounded-md border bg-card px-2 py-1.5 leading-snug">
                    {d.chosen}
                  </div>
                </div>
              )}
              {v3Options && v3Options.length > 0 && (
                <div>
                  <div className="font-medium text-foreground text-[10px] uppercase tracking-wide mb-0.5">
                    Options considered
                  </div>
                  <ul className="space-y-1">
                    {v3Options.map((opt) => (
                      <li
                        key={`${d.id}:opt:${opt}`}
                        className={cn(
                          "rounded-md border px-2 py-1.5 leading-snug",
                          d.chosen && opt === d.chosen
                            ? "border-primary/40 bg-primary/5 text-foreground/90"
                            : "bg-card text-muted-foreground",
                        )}
                      >
                        {opt}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {!v3Options && v2Options && (
                <div>
                  <div className="font-medium text-foreground text-[10px] uppercase tracking-wide mb-0.5">
                    Options considered
                  </div>
                  <div
                    className="prose-sm max-w-none [&_p]:my-1 [&_code]:font-mono"
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(v2Options) }}
                  />
                </div>
              )}
              {d.context && (
                <div>
                  <div className="font-medium text-foreground text-[10px] uppercase tracking-wide mb-0.5">
                    Context
                  </div>
                  <div
                    className="prose-sm max-w-none [&_p]:my-1 [&_code]:font-mono"
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(d.context) }}
                  />
                </div>
              )}
              {/* v0.2 fallback sections — kept readable for legacy docs until
                  those re-upload; v0.3 docs won't populate these. */}
              {d.decision && (
                <div>
                  <div className="font-medium text-foreground text-[10px] uppercase tracking-wide mb-0.5">
                    Decision
                  </div>
                  <div
                    className="prose-sm max-w-none [&_p]:my-1 [&_code]:font-mono"
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(d.decision) }}
                  />
                </div>
              )}
              {d.consequences && (
                <div>
                  <div className="font-medium text-foreground text-[10px] uppercase tracking-wide mb-0.5">
                    Consequences
                  </div>
                  <div
                    className="prose-sm max-w-none [&_p]:my-1 [&_code]:font-mono"
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(d.consequences) }}
                  />
                </div>
              )}
            </div>
          </details>
        );
      })}
    </div>
  );
}

function OpsNoteCards({ notes }: { notes: OperationalNote[] }) {
  if (notes.length === 0) return null;
  return (
    <div className="space-y-2">
      {notes.map((n) => {
        const tone =
          n.severity === "critical"
            ? "border-danger/30 bg-danger-soft"
            : n.severity === "warning"
              ? "border-warning/30 bg-warning-soft"
              : "border-info/30 bg-info-soft";
        return (
          <div key={n.id} className={cn("text-xs rounded-md border px-3 py-2", tone)}>
            <div className="flex items-center gap-2 font-medium">
              <Info className="h-3.5 w-3.5" />
              <span>{n.label}</span>
              <span className="text-[10px] uppercase tracking-wide opacity-60 ml-auto">
                {n.type}
              </span>
            </div>
            {n.why && (
              <div className="mt-1 text-[11px] italic opacity-90">
                <span className="font-medium uppercase tracking-wide text-[9px] mr-1 opacity-70">
                  Why:
                </span>
                {n.why}
              </div>
            )}
            {n.content && (
              <div
                className="prose-sm max-w-none mt-1 [&_p]:my-1 [&_code]:font-mono [&_table]:text-[10px] [&_table]:my-1 [&_table]:border-collapse [&_th]:border [&_th]:px-1.5 [&_th]:py-0.5 [&_td]:border [&_td]:px-1.5 [&_td]:py-0.5"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(n.content) }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function RelatedFlows({ flows }: { flows: DataFlow[] }) {
  const { dispatch } = useArchViz();
  if (flows.length === 0) return null;
  return (
    <div className="space-y-1.5">
      {flows.map((f) => {
        const stepCount = f.steps?.length ?? 0;
        // v0.3 flows surface what_data ?? why_this_shape; v0.2 flows surface
        // step count. The flow narrative panel handles the deeper view.
        const v3Detail = f.what_data ?? f.why_this_shape;
        return (
          <button
            type="button"
            key={f.id}
            onClick={() => dispatch({ type: "START_FLOW", flowId: f.id })}
            className="w-full text-left text-xs rounded-md border bg-card hover:bg-muted transition-colors px-3 py-2"
          >
            <div className="flex items-center gap-2">
              <Play className="h-3 w-3 shrink-0" />
              <span className="font-medium truncate">{f.label}</span>
              {stepCount > 0 && (
                <span className="text-[10px] text-muted-foreground ml-auto shrink-0">
                  {stepCount} step{stepCount === 1 ? "" : "s"}
                </span>
              )}
            </div>
            {v3Detail && !stepCount && (
              <div className="mt-1 text-[11px] text-muted-foreground/90 truncate">{v3Detail}</div>
            )}
            {(f.why ?? f.why_this_shape) && (
              <div className="mt-1 text-[11px] italic text-muted-foreground border-l-2 border-primary/30 pl-2">
                {f.why ?? f.why_this_shape}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </div>
      {children}
    </div>
  );
}

const SEVERITY_BADGE_CLASS: Record<Risk["severity"], string> = {
  info: "bg-info-soft text-info-ink border-info/30",
  warning: "bg-warning-soft text-warning-ink border-warning/30",
  critical: "bg-danger-soft text-danger-ink border-danger/30",
};

const CONSENT_BADGE_CLASS: Record<string, string> = {
  delegated: "bg-info-soft text-info-ink border-info/30",
  application: "bg-warning-soft text-warning-ink border-warning/30",
  other: "bg-surface-tint text-ink-subtle border-border",
};

function PermissionDetail({
  permission,
  dispatch,
}: {
  permission: Permission;
  dispatch: (a: { type: "SELECT_NODE"; nodeId: string }) => void;
}) {
  const consentClass = CONSENT_BADGE_CLASS[permission.consent_surface] ?? CONSENT_BADGE_CLASS.other;
  return (
    <>
      <div className="flex items-center gap-2">
        <Badge
          variant="outline"
          className={cn("text-[10px] uppercase tracking-wider border", consentClass)}
        >
          {permission.consent_surface}
        </Badge>
        <Badge
          variant="outline"
          className={cn(
            "text-[10px] uppercase tracking-wider border",
            SEVERITY_BADGE_CLASS[permission.severity],
          )}
        >
          {permission.severity}
        </Badge>
      </div>
      <Section title="What it enables">
        <p className="text-sm leading-relaxed">{permission.what_it_enables}</p>
      </Section>
      <Section title="What breaks if revoked or leaked">
        <p className="text-sm leading-relaxed">{permission.what_breaks}</p>
      </Section>
      {permission.endpoints && permission.endpoints.length > 0 && (
        <Section title={`Endpoints gated by this scope (${permission.endpoints.length})`}>
          <ul className="space-y-1">
            {permission.endpoints.map((endpointId) => (
              <li key={endpointId}>
                <button
                  type="button"
                  className="text-sm font-mono text-foreground/80 hover:text-foreground hover:underline text-left"
                  onClick={() =>
                    dispatch({ type: "SELECT_NODE", nodeId: `endpoint:${endpointId}` })
                  }
                >
                  {endpointId}
                </button>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </>
  );
}

const VERB_BADGE_CLASS: Record<string, string> = {
  GET: "bg-success-soft text-success-ink border-success/30",
  POST: "bg-info-soft text-info-ink border-info/30",
  PUT: "bg-warning-soft text-warning-ink border-warning/30",
  PATCH: "bg-warning-soft text-warning-ink border-warning/30",
  DELETE: "bg-danger-soft text-danger-ink border-danger/30",
  HEAD: "bg-surface-tint text-ink-subtle border-border",
  OPTIONS: "bg-surface-tint text-ink-subtle border-border",
};

function EndpointDetail({
  endpoint,
  dispatch,
}: {
  endpoint: ApiEndpoint;
  dispatch: (a: { type: "SELECT_NODE"; nodeId: string }) => void;
}) {
  const severity = endpoint.severity ?? "info";
  return (
    <>
      <div className="flex items-center gap-2">
        <Badge
          variant="outline"
          className={cn(
            "text-[10px] font-bold font-mono border",
            VERB_BADGE_CLASS[endpoint.verb] ?? VERB_BADGE_CLASS.HEAD,
          )}
        >
          {endpoint.verb}
        </Badge>
        <Badge
          variant="outline"
          className={cn(
            "text-[10px] uppercase tracking-wider border",
            SEVERITY_BADGE_CLASS[severity],
          )}
        >
          {severity}
        </Badge>
      </div>
      <Section title="What">
        <p className="text-sm leading-relaxed">{endpoint.what}</p>
      </Section>
      <Section title="Why">
        <p className="text-sm leading-relaxed">{endpoint.why}</p>
      </Section>
      <Section title="Blast radius">
        <p className="text-sm leading-relaxed">{endpoint.blast_radius}</p>
      </Section>
      {endpoint.called_by.length > 0 && (
        <Section title="Called by">
          <div className="flex flex-wrap gap-1.5">
            {endpoint.called_by.map((c) => (
              <Badge key={c} variant="secondary" className="font-mono text-[10px]">
                {c}
              </Badge>
            ))}
          </div>
        </Section>
      )}
      {endpoint.permission && (
        <Section title="Requires permission">
          <button
            type="button"
            className="text-sm font-mono text-foreground/80 hover:text-foreground hover:underline"
            onClick={() =>
              dispatch({ type: "SELECT_NODE", nodeId: `permission:${endpoint.permission}` })
            }
          >
            {endpoint.permission}
          </button>
        </Section>
      )}
    </>
  );
}

function CollapsibleSection({
  id,
  title,
  count,
  icon: Icon,
  defaultOpen = false,
  forceOpenSignal,
  children,
}: {
  id?: string;
  title: string;
  count: number;
  icon: typeof AlertCircle;
  defaultOpen?: boolean;
  forceOpenSignal?: number;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => {
    if (forceOpenSignal !== undefined && forceOpenSignal > 0) setOpen(true);
  }, [forceOpenSignal]);
  return (
    <div id={id} className="border-t pt-3 -mx-1 px-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 text-left group"
        aria-expanded={open}
      >
        <ChevronRight
          className={cn(
            "h-3.5 w-3.5 text-muted-foreground transition-transform shrink-0",
            open && "rotate-90",
          )}
        />
        <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <span className="text-eyebrow font-bold uppercase tracking-eyebrow text-ink-subtle flex-1">
          {title}
        </span>
        <span className="font-mono text-[9.5px] font-semibold px-1.5 py-0.5 rounded-sm bg-surface-tint text-ink-muted tabular-nums">
          {count}
        </span>
      </button>
      {open && <div className="mt-3 pl-5">{children}</div>}
    </div>
  );
}

function PanelDrillCTA({
  label,
  helper,
  onClick,
}: {
  label: string;
  helper: string;
  onClick: () => void;
}) {
  return (
    <div className="pt-2 space-y-1.5">
      <Button
        type="button"
        onClick={onClick}
        className="group w-full justify-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90"
      >
        <span>{label}</span>
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </Button>
      <p className="text-meta italic text-ink-subtle text-center">{helper}</p>
    </div>
  );
}

export function DetailPanel() {
  const { state, dispatch, extras } = useArchViz();
  const arch = state.architecture;

  useEffect(() => {
    if (state.scrollToRisksTick === 0) return;
    const id = requestAnimationFrame(() => {
      const el = document.getElementById("detail-panel-risks-section");
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(id);
  }, [state.scrollToRisksTick]);

  const resolved = useMemo(
    () => (arch ? resolveSelection(arch, state.selectedNodeId) : null),
    [arch, state.selectedNodeId],
  );

  const matchingDecisions = useMemo(() => {
    if (!arch || !resolved) return [];
    const id = resolved.entity.id;
    return arch.decisions.filter((d) => (d.applies_to ?? []).includes(id));
  }, [arch, resolved]);

  const matchingOpsNotes = useMemo(() => {
    if (!arch || !resolved) return [];
    const id = resolved.entity.id;
    return arch.operationalNotes.filter((n) => (n.applies_to ?? []).includes(id));
  }, [arch, resolved]);

  const matchingFlows = useMemo(() => {
    if (!arch || !resolved) return [];
    const id = resolved.entity.id;
    // v0.3 flows: from/to are single endpoints. v0.2 flows: steps[] is the
    // narrative — match if any step touches this entity. A flow that
    // populates both shapes (during migration) matches on either.
    return arch.dataFlows.filter((f) => {
      if (f.from === id || f.to === id) return true;
      return (f.steps ?? []).some((s) => s.source === id || s.target === id);
    });
  }, [arch, resolved]);

  const [width, setWidth] = useState<number>(() => loadInitialWidth());
  const [isResizing, setIsResizing] = useState(false);
  const widthRef = useRef(width);
  widthRef.current = width;

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(WIDTH_STORAGE_KEY, String(width));
  }, [width]);

  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = widthRef.current;
    setIsResizing(true);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";

    const onMove = (ev: MouseEvent) => {
      const dx = startX - ev.clientX;
      const next = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth + dx));
      setWidth(next);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      setIsResizing(false);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, []);

  if (!arch) return null;

  return (
    <AnimatePresence initial={false}>
      {resolved && !state.risksSheetOpen && (
        <motion.aside
          key="detail-panel"
          initial={{ width: 0, opacity: 0 }}
          animate={{ width, opacity: 1 }}
          exit={{ width: 0, opacity: 0 }}
          transition={{ duration: isResizing ? 0 : 0.22, ease: "easeOut" }}
          className="border-l bg-card shrink-0 relative overflow-hidden"
          style={{ minWidth: 0 }}
        >
          <button
            type="button"
            aria-label="Resize panel"
            onMouseDown={startResize}
            onDoubleClick={() => setWidth(DEFAULT_WIDTH)}
            className={cn(
              "group absolute left-0 top-0 bottom-0 w-1.5 z-20 cursor-col-resize",
              "hover:bg-primary/30 transition-colors",
              isResizing && "bg-primary/40",
            )}
          >
            <span className="absolute inset-y-0 left-0 w-px bg-border group-hover:bg-primary/60" />
          </button>

          <div
            className="h-full flex flex-col"
            style={{ width, minWidth: MIN_WIDTH, maxWidth: MAX_WIDTH }}
          >
            <div className="shrink-0 border-b bg-card/95 backdrop-blur px-4 pl-5 py-3 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-eyebrow font-bold uppercase tracking-eyebrow text-ink-subtle">
                  {resolved.kind === "component" && resolved.container
                    ? `Component · ${resolved.container.label}`
                    : resolved.kind === "entity" && resolved.container
                      ? `Entity · ${resolved.container.label}`
                      : resolved.kind === "system"
                        ? "System"
                        : resolved.kind === "question"
                          ? "Question"
                          : resolved.kind === "permission" && resolved.external
                            ? `Permission · ${resolved.external.label}`
                            : resolved.kind === "endpoint" && resolved.external
                              ? `Endpoint · ${resolved.external.label}`
                              : resolved.kind.replace("-", " ")}
                </div>
                <h2 className="text-h-panel font-bold tracking-tighter text-ink-heading leading-tight truncate">
                  {resolved.kind === "question"
                    ? resolved.entity.title
                    : resolved.kind === "permission"
                      ? resolved.entity.scope
                      : resolved.kind === "endpoint"
                        ? `${resolved.entity.verb} ${resolved.entity.url_pattern}`
                        : resolved.entity.label}
                </h2>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => dispatch({ type: "SELECT_NODE", nodeId: null })}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 pl-5 py-4 space-y-5">
              {resolved.kind === "question" ? (
                <QuestionPanel
                  question={resolved.entity}
                  resolvedDecision={
                    resolved.entity.answered_by
                      ? arch.decisions.find((d) => d.id === resolved.entity.answered_by)
                      : undefined
                  }
                  onSelectDecision={(decisionId) => {
                    // Decisions are panel-only too — set the selection so any
                    // related side panels (or future decision card view) pick
                    // it up. Same selector channel the rest of the UI uses.
                    dispatch({ type: "SELECT_NODE", nodeId: `decision:${decisionId}` });
                  }}
                />
              ) : resolved.kind === "permission" ? (
                <PermissionDetail permission={resolved.entity} dispatch={dispatch} />
              ) : resolved.kind === "endpoint" ? (
                <EndpointDetail endpoint={resolved.entity} dispatch={dispatch} />
              ) : (
                <>
                  {resolved.kind === "system" && (
                    <>
                      {arch.meta.lifecycle_stage && (
                        <Section title="Lifecycle stage">
                          <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                            {arch.meta.lifecycle_stage}
                          </Badge>
                        </Section>
                      )}
                      {resolved.entity.headline && (
                        <Section title="Headline">
                          <p className="text-sm leading-relaxed">{resolved.entity.headline}</p>
                        </Section>
                      )}
                    </>
                  )}

                  <Section title="What">
                    {resolved.entity.description ? (
                      <p className="text-sm leading-relaxed">{resolved.entity.description}</p>
                    ) : (
                      <p className="text-xs italic text-muted-foreground">
                        No description in the markdown. Add a{" "}
                        <code className="font-mono">description</code> field to surface it here.
                      </p>
                    )}
                  </Section>

                  <Section title="Why">
                    {(() => {
                      // Actors use `why_it_matters` in v0.3 and `why` in v0.2; the
                      // legacy mapper preserves both. Prefer canonical; fall back.
                      const entityWhy =
                        resolved.kind === "actor"
                          ? (resolved.entity.why_it_matters ?? resolved.entity.why)
                          : resolved.entity.why;
                      const hasNarrative =
                        resolved.kind === "container" && Boolean(resolved.entity.narrative);
                      const decisionsWithContext = matchingDecisions.filter((d) => d.context);
                      const hasAny =
                        Boolean(entityWhy) || hasNarrative || decisionsWithContext.length > 0;
                      if (!hasAny) {
                        return (
                          <p className="text-xs italic text-muted-foreground">
                            No rationale in the markdown. Add a{" "}
                            <code className="font-mono">why</code> field to this entry, or a related
                            ## Decisions entry with{" "}
                            <code className="font-mono">applies_to: [this id]</code>.
                          </p>
                        );
                      }
                      return (
                        <div className="space-y-3">
                          {entityWhy && <p className="text-sm leading-relaxed">{entityWhy}</p>}
                          {hasNarrative && resolved.kind === "container" && (
                            <NarrativeBlock markdown={resolved.entity.narrative as string} />
                          )}
                          {decisionsWithContext.map((d) => (
                            <div
                              key={d.id}
                              className="text-sm border-l-2 border-primary/30 pl-3 py-1 text-muted-foreground"
                            >
                              <div className="text-[10px] uppercase tracking-wider text-foreground mb-0.5">
                                from {d.title ?? d.label}
                              </div>
                              <div
                                className="prose-sm max-w-none [&_p]:my-1 [&_code]:font-mono"
                                dangerouslySetInnerHTML={{
                                  __html: renderMarkdown(d.context ?? ""),
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </Section>

                  {(resolved.kind === "container" || resolved.kind === "component") &&
                    (() => {
                      // Read canonical tech_stack with v0.2 technology fallback —
                      // mapper populates both, so this is defensive only.
                      const techList =
                        resolved.entity.tech_stack ?? resolved.entity.technology ?? [];
                      if (techList.length === 0) return null;
                      return (
                        <Section title="Technology">
                          <TechnologyBadges technology={techList} />
                        </Section>
                      );
                    })()}

                  {(resolved.kind === "container" ||
                    resolved.kind === "component" ||
                    resolved.kind === "external-system") &&
                    resolved.entity.cost !== undefined && (
                      <Section title="Cost">
                        <CostIndicator cost={resolved.entity.cost} />
                      </Section>
                    )}

                  {resolved.kind === "external-system" && resolved.entity.url && (
                    <Section title="URL">
                      <a
                        href={resolved.entity.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs font-mono flex items-center gap-1 hover:underline break-all"
                      >
                        {resolved.entity.url}
                        <ExternalLink className="h-3 w-3 shrink-0" />
                      </a>
                    </Section>
                  )}

                  {(resolved.kind === "container" ||
                    resolved.kind === "component" ||
                    resolved.kind === "external-system" ||
                    resolved.kind === "entity") &&
                    (() => {
                      const risks = resolved.entity.risks ?? [];
                      if (risks.length === 0) return null;
                      return (
                        <CollapsibleSection
                          id="detail-panel-risks-section"
                          title="Risks"
                          count={risks.length}
                          icon={ShieldAlert}
                          defaultOpen
                          forceOpenSignal={state.scrollToRisksTick}
                        >
                          <RiskCards risks={risks} />
                        </CollapsibleSection>
                      );
                    })()}

                  {resolved.kind === "entity" &&
                    (() => {
                      const examples = resolved.entity.examples ?? [];
                      if (examples.length === 0) return null;
                      return (
                        <CollapsibleSection
                          title="Examples of Data"
                          count={examples.length}
                          icon={ScrollText}
                          defaultOpen
                        >
                          <ul className="space-y-1.5 text-sm leading-relaxed list-disc pl-5 marker:text-ink-subtle">
                            {examples.map((ex) => (
                              <li key={ex}>{ex}</li>
                            ))}
                          </ul>
                        </CollapsibleSection>
                      );
                    })()}

                  {resolved.kind === "entity" &&
                    (() => {
                      const notable = resolved.entity.notable ?? [];
                      if (notable.length === 0) return null;
                      return (
                        <CollapsibleSection
                          title="Notable Items"
                          count={notable.length}
                          icon={Info}
                          defaultOpen
                        >
                          <ul className="space-y-1.5 text-sm leading-relaxed list-disc pl-5 marker:text-ink-subtle">
                            {notable.map((n) => (
                              <li key={n}>{n}</li>
                            ))}
                          </ul>
                        </CollapsibleSection>
                      );
                    })()}

                  {(resolved.kind === "container" ||
                    resolved.kind === "component" ||
                    resolved.kind === "external-system") &&
                    (() => {
                      const assumptions = resolved.entity.assumptions ?? [];
                      if (assumptions.length === 0) return null;
                      return (
                        <CollapsibleSection
                          title="Assumptions"
                          count={assumptions.length}
                          icon={HelpCircle}
                          defaultOpen={assumptions.some((a) => !a.verified)}
                        >
                          <AssumptionsList assumptions={assumptions} />
                        </CollapsibleSection>
                      );
                    })()}

                  {matchingDecisions.length > 0 && (
                    <CollapsibleSection
                      title="Related Decisions"
                      count={matchingDecisions.length}
                      icon={ScrollText}
                    >
                      <DecisionCards decisions={matchingDecisions} />
                    </CollapsibleSection>
                  )}

                  {matchingOpsNotes.length > 0 && (
                    <CollapsibleSection
                      title="Operational Notes"
                      count={matchingOpsNotes.length}
                      icon={Wrench}
                    >
                      <OpsNoteCards notes={matchingOpsNotes} />
                    </CollapsibleSection>
                  )}

                  {matchingFlows.length > 0 && (
                    <CollapsibleSection
                      title="Related Flows"
                      count={matchingFlows.length}
                      icon={Workflow}
                    >
                      <RelatedFlows flows={matchingFlows} />
                    </CollapsibleSection>
                  )}
                </>
              )}

              {resolved.kind === "system" && (
                <PanelDrillCTA
                  label="Drill into containers"
                  helper="Click any container to inspect, or double-click to drill further."
                  onClick={() => dispatch({ type: "JUMP_TO_LEVEL", level: 2 })}
                />
              )}
              {resolved.kind === "container" && (
                <PanelDrillCTA
                  label="Drill into components"
                  helper="Click any component to inspect."
                  onClick={() =>
                    dispatch({
                      type: "DRILL_INTO_CONTAINER",
                      containerId: resolved.entity.id,
                    })
                  }
                />
              )}

              {extras.versionId && !extras.commentsHidden && (
                <CollapsibleSection
                  title="Discussion"
                  count={extras.commentCounts[resolved.entity.id] ?? 0}
                  icon={MessageSquare}
                >
                  <CommentsSection
                    versionId={extras.versionId}
                    elementId={resolved.entity.id}
                    elementType={resolved.kind}
                    currentUserId={extras.currentUserId}
                  />
                </CollapsibleSection>
              )}
            </div>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
